// The front door (/). The server sets every word on the page; this module adds
// what needs a browser: the live spiral behind the words, the errand's copy
// button, and signing in as a visitor by name.
// (P144) The spiral is an arcade cabinet's attract mode: it opens on the whole
// spiral, so the first thing seen is a world, then the camera glides down to the
// neighbourhood round one real plazmi and on to the next as they do things right
// now (/api/now and /api/homestead), lingering about ten seconds on each, with
// one calm caption ("Wren is mowing the lawn · plot 12") and "Follow this one",
// which goes to that plazmi's page and follows it there (follow.ts). After every
// two it goes back up to the whole spiral. It never goes so close that the
// islands round a home leave the frame. Glides lift a little over the islands
// between homes and never hurry.
// The visitor can take the camera at any time, as on the house pages
// (camctl.ts): drag turns; right-drag, shift-drag or two fingers move; a
// pinch or ⌘/Ctrl+wheel comes closer; WASD, Q/E, +/- and the brass dial too
// (put away until the camera is taken); R or the dial's middle goes back, and
// the glides start again. Half a minute untouched, they start again anyway.
// A click on a home visits it; a drag never does. The page's scroll isn't
// trapped: a plain wheel scrolls the page until the visitor takes the spiral
// in hand. On touch one finger scrolls the page until a tap on the spiral's
// sky takes it in hand; then it drags the map until "Done". Two fingers are
// always the spiral's.
// On a very small screen, or without WebGL, the sky shows a real postcard
// instead; with reduced motion the camera holds the whole spiral and only the
// caption changes.
import './perf/singlepass.js'; // (P84) first: before any material is made
import * as THREE from 'three';
import { setStyle } from './kit.js';
import { makeRenderer, makeStage, makeCamera, clouds, island } from './stage.js';
import { makeComposer } from './post.js';
import { fitView } from './fit.js';
import { govern, startPR, watchFrom } from './quality.js';
import { early, stage as loadStage } from './loading.js';
import { visit, visiting } from './arrive.js';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { camControls, clickGuard } from './camctl.js';
import { bakedMaterials, doorSpot, Folk, Notes, type Bridge, type Isle } from './neighbourhood.js';
import { SpiralMap, fitShadow, nearBudget } from './spiralmap.js';

type Home = Isle & { resident: { name: string; emoji?: string; said?: string | null } };
type Lot = { home: Home; at: THREE.Vector3 };
// /api/now's residents (follow.clj now-json): public fields only
type NowP = { slug: string; name: string; emoji?: string; where: { name: string; kind: string; page: string }; group: string; doing: string; busy: boolean };
// who the camera is with: the resident, the home it's shown at, and the words
type Subject = { slug: string; name: string; host: string; text: string; plot: number; href: string; score: number; away?: boolean };
// (G4a) the front page frames the first spiral, however many homes there are beyond it
const FIRST = 120;
// up to this many homes, all of them are on the front page (G4a measured the spiral to 2,000)
const ALL = 2000;
// (P144) homes drawn in full at once, round whoever the camera is with
const NEAR = 40;
// (P144) how long the camera stays with each plazmi, and how long a glide takes (seconds)
const HOLD = 10, HOLD_QUIET = 14, GLIDE = 5.5;
// how long the first view of the whole spiral holds (s), and how many plazmi between views of it
const OPEN_HOLD = 9, BETWEEN = 2;
// untouched this long after the visitor let go, the glides start again (ms)
const RESUME = 30000;

const q = new URLSearchParams(location.search);
// (looks) a ?look= trial of the first screen says what it wants of the world on #hero (front-a.html):
// data-near-phone / -wide, how close the camera comes; data-elev-phone / -wide, how low; data-at, the
// home to open on; data-spot, the empty plot to open on; data-turn, the angle round it; data-stay, no
// tour. A script tag #look-lines holds real words for the tour. The plain page says none of it.
const LOOK: Record<string, string | undefined> = { ...(document.getElementById('hero')?.dataset || {}) };
const LINES: { slug: string; name: string; emoji?: string; plot: number; line: string }[] | null = (() => {
  try { return JSON.parse(document.getElementById('look-lines')?.textContent || 'null'); } catch { return null; }
})();
const stats = { fps: 0, pr: 0, homes: 0, mode: '', dist: 0 };
(window as any).__plazmi = stats;
const done = () => { (window as any).__ready = true; };
const $ = <T extends HTMLElement>(id: string) => document.getElementById(id) as T | null;

// ----- the errand
async function copyText(text: string) {
  try { await navigator.clipboard.writeText(text); return true; } catch { /* the old way, below */ }
  const ta = document.createElement('textarea');
  ta.value = text; ta.setAttribute('readonly', ''); ta.style.position = 'fixed'; ta.style.opacity = '0';
  document.body.appendChild(ta); ta.select();
  let ok = false;
  try { ok = document.execCommand('copy'); } catch { /* fine */ }
  ta.remove();
  return ok;
}
// The card is the copy button: a tap anywhere on it copies the errand, signed
// with the name typed on its FROM line (kept on this browser only).
function errand() {
  const card = $('errand-card'), msg = $('errand'), btn = $<HTMLButtonElement>('errand-copy'), said = $('copied');
  const from = $<HTMLInputElement>('from'), sign = $('errand-sign'), signName = $('errand-name');
  if (!card || !btn) return;
  const ink = btn.querySelector('.ink') || btn;
  const KEY = 'plazmi-errand-from';
  const name = () => (from?.value || '').trim();
  const showName = () => {
    const n = name();
    if (sign) sign.hidden = !n;
    if (signName) signName.textContent = n;
  };
  try { if (from && !from.value) from.value = localStorage.getItem(KEY) || ''; } catch { /* a private window */ }
  showName();
  from?.addEventListener('input', () => {
    showName();
    try { localStorage.setItem(KEY, name()); } catch { /* fine */ }
  });
  const text = () => (card.dataset.errand || '') + (name() ? `\n\n— from ${name()}` : '');
  let timer = 0;
  const go = async () => {
    const ok = await copyText(text());
    clearTimeout(timer);
    card.classList.remove('done');
    if (ok) {
      void card.offsetWidth; // restart the stamp's thump
      card.classList.add('done');
      ink.textContent = 'Copied ✓';
      if (said) said.textContent = 'Copied. Paste it to your agent.';
      remember('send');
    } else if (msg) {
      // no clipboard here: select the words so they can be copied by hand
      const range = document.createRange();
      range.selectNodeContents(msg);
      const sel = getSelection();
      sel?.removeAllRanges(); sel?.addRange(range);
      if (said) said.textContent = 'The words are selected: press Ctrl+C (⌘C on a Mac) to copy them.';
    }
    timer = window.setTimeout(() => { card.classList.remove('done'); ink.textContent = 'Copy for my agent'; if (said) said.textContent = ''; }, 3200);
  };
  btn.addEventListener('click', (e) => { e.stopPropagation(); go(); });
  card.addEventListener('click', (e) => {
    if ((e.target as HTMLElement).closest('input, label, a, .lines')) return;
    if ((getSelection()?.toString() || '').length > 0) return; // selecting by hand
    go();
  });
}

// ----- signing in as a visitor: Google's button (server-drawn), an emailed link, or a name in local dev
function hello() {
  const form = $('hello'), named = $<HTMLFormElement>('hello-name'), note = $('hello-note'), me = $('me'), input = $<HTMLInputElement>('who');
  const box = $<HTMLDetailsElement>('who-box');
  emailed(form?.querySelector<HTMLFormElement>('form.em-form') || null);
  if (!form || !named || !input) return;
  // opened, the name field is ready to type in; a click elsewhere folds it away
  box?.addEventListener('toggle', () => { if (box.open && !form.classList.contains('visiting') && matchMedia('(pointer: fine)').matches) setTimeout(() => input.focus(), 0); });
  addEventListener('pointerdown', (e) => { if (box?.open && !box.contains(e.target as Node)) box.open = false; });
  // (P95) a quiet sign out, for this browser
  $('signout')?.addEventListener('click', async (e) => {
    e.preventDefault();
    await fetch('/auth/signout', { method: 'POST', headers: { accept: 'application/json' } }).catch(() => null);
    location.reload();
  });
  named.addEventListener('submit', async (e) => {
    e.preventDefault();
    const name = input.value.trim();
    if (!name) { input.focus(); return; }
    try {
      const r = await fetch('/api/visitors', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ name }) });
      const j = await r.json();
      if (j.ok) {
        form.classList.add('visiting'); box?.classList.add('visiting');
        if (me) me.textContent = j.name || name;
        const sum = box?.querySelector('summary');
        if (sum) sum.textContent = j.name || name;
        if (note) note.textContent = 'Say hello and sign guestbooks at any home. Off you go.';
      } else if (note) note.textContent = j.error || 'That name didn’t take. Try another?';
    } catch { if (note) note.textContent = 'The post didn’t get through. Try again in a moment.'; }
  });
}

// ----- (M1) the emailed sign-in link and the footer's news box: sent without leaving the page
// (without the script, each form posts and the server answers with a page of its own)
async function postFor(form: HTMLFormElement): Promise<string> {
  const f = new FormData(form), body: Record<string, string | boolean> = {};
  f.forEach((v, k) => { body[k] = k === 'news' ? true : String(v); });
  try {
    const r = await fetch(form.action, { method: 'POST', headers: { 'content-type': 'application/json', accept: 'application/json' }, body: JSON.stringify(body) });
    const j = await r.json();
    return j.text || j.error || 'The post didn’t get through. Try again in a moment.';
  } catch { return 'The post didn’t get through. Try again in a moment.'; }
}

function emailed(form: HTMLFormElement | null) {
  if (!form) return;
  const fold = form.closest('details');
  fold?.addEventListener('toggle', () => { if (fold.open && matchMedia('(pointer: fine)').matches) setTimeout(() => form.querySelector<HTMLInputElement>('input[type=email]')?.focus(), 0); });
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const go = form.querySelector('button'); if (go) go.disabled = true;
    const text = await postFor(form);
    const said = document.createElement('p'); said.className = 'em-sent'; said.setAttribute('role', 'status'); said.textContent = text;
    form.replaceWith(said);
  });
}

function news() {
  const form = $<HTMLFormElement>('news'), said = $('news-said');
  if (!form) return;
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const go = form.querySelector('button'); if (go) go.disabled = true;
    const text = await postFor(form);
    if (said) said.textContent = text;
    if (go) go.disabled = false;
    const input = form.querySelector<HTMLInputElement>('input[type=email]'); if (input && !/look like/.test(text)) input.value = '';
  });
}

// ----- (P144) more places folds away when you click elsewhere
function more() {
  const m = $<HTMLDetailsElement>('more');
  if (!m) return;
  addEventListener('pointerdown', (e) => { if (m.open && !m.contains(e.target as Node)) m.open = false; });
  addEventListener('keydown', (e) => { if (e.key === 'Escape' && m.open) { m.open = false; m.querySelector('summary')?.focus(); } });
}

// ----- (P144) coming back: the place used last is a tap away, beside the two ways in
const LAST = 'plazmi.front.last';
function remember(v: string, label?: string, href?: string) { try { localStorage.setItem(LAST, JSON.stringify({ v, label, href })); } catch { /* fine */ } }
function returning() {
  for (const a of document.querySelectorAll<HTMLAnchorElement>('[data-last]')) {
    a.addEventListener('click', () => {
      const v = a.dataset.last || '';
      // a place keeps its own name; following keeps who was followed (set when it starts, below)
      if (v !== 'follow') remember(v, (a.firstChild?.textContent || a.textContent || '').trim());
    });
  }
  let last: { v: string; label?: string; href?: string } | null = null;
  try { last = JSON.parse(localStorage.getItem(LAST) || 'null'); } catch { /* fine */ }
  const back = $<HTMLAnchorElement>('back-to');
  if (!last || !back) return;
  // "What everyone's up to" is the page's own first button, and /new is an explainer, not a place: no "Back to" either
  if (last.v.startsWith('/') && last.label && last.v !== '/now' && last.v !== '/new') {
    back.href = last.v; back.textContent = `Back to ${last.label.replace(/^The /, 'the ')}`;
    back.classList.add('on');
  } else if (last.v === 'follow' && last.label && last.href) {
    back.href = last.href; back.textContent = `Follow ${last.label} again`;
    back.classList.add('on');
  }
}

// ----- the sky: a still postcard, when there's no spiral to draw
function stillCard(sky: HTMLElement) {
  const src = (document.querySelector('.mail .pc img') as HTMLImageElement | null)?.getAttribute('src');
  if (src) {
    const img = new Image();
    img.className = 'still'; img.alt = ''; img.src = src;
    sky.appendChild(img);
  }
  stats.mode = 'still';
  done();
}

function webgl() {
  try { const c = document.createElement('canvas'); return !!(c.getContext('webgl2') || c.getContext('webgl')); } catch { return false; }
}

const ease = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
const followHref = (page: string, slug: string) => {
  const u = new URL(page || `/@${slug}`, location.href);
  u.searchParams.set('follow', slug); u.searchParams.set('intro', '1');
  return u.pathname + u.search;
};

async function spiral(sky: HTMLElement) {
  const calm = matchMedia('(prefers-reduced-motion: reduce)').matches || q.has('still');
  // (the page's head asked for the spiral already, alongside the bundle: loading.ts)
  const data = (await early('/api/homestead')) as { homes: Home[]; bridges: Bridge[]; now: number };
  loadStage(2);
  const everyone = data.homes || [];
  const bridges = data.bridges || [];
  let now0 = data.now, wall0 = performance.now();
  const serverNow = () => now0 + (performance.now() - wall0);
  setStyle('A');
  let vw = sky.clientWidth, vh = sky.clientHeight;
  const narrow = () => innerWidth <= 900;
  const dpr = devicePixelRatio || 1;
  const prCap = Math.min(narrow() ? 1.25 : 1.5, dpr);
  let PR = +(q.get('pr') || Math.min(prCap, startPR()));
  const renderer = makeRenderer(vw, vh, PR);
  sky.prepend(renderer.domElement);
  renderer.shadowMap.autoUpdate = false; renderer.shadowMap.needsUpdate = true;

  // the spiral's middle and reach, from where the islands really float (the first spiral's)
  const first = everyone.filter((h) => h.plot_number <= FIRST);
  const box = new THREE.Box3();
  for (const h of first) {
    box.expandByPoint(new THREE.Vector3(h.pos[0] - h.plot[0] / 2, 0, h.pos[2] - h.plot[1] / 2));
    box.expandByPoint(new THREE.Vector3(h.pos[0] + h.plot[0] / 2, 0, h.pos[2] + h.plot[1] / 2));
  }
  if (!first.length) box.expandByPoint(new THREE.Vector3(-8, 0, -8)).expandByPoint(new THREE.Vector3(8, 0, 8));
  const lone = !first.length;
  const target = box.getCenter(new THREE.Vector3()).setY(0);
  let reach = 10;
  for (const h of first) for (const [sx, sz] of [[-1, -1], [1, -1], [-1, 1], [1, 1]]) {
    reach = Math.max(reach, Math.hypot(h.pos[0] + (sx * h.plot[0]) / 2 - target.x, h.pos[2] + (sz * h.plot[1]) / 2 - target.z));
  }
  if (lone) reach = 30; // one empty plot, small in a big sky
  const span = reach + 14;
  // (P144) every home, so the glides can reach a newcomer at the edge (spiralmap.ts draws only the
  // nearest in full, as /homestead does); past ALL homes, the first spiral, a turn beyond it and the
  // newest, the world further out isn't drawn here
  const newest = new Set([...everyone].sort((a, b) => b.plot_number - a.plot_number).slice(0, 200).map((h) => h.slug));
  const homes = everyone.length <= ALL ? everyone
    : everyone.filter((h) => h.plot_number <= FIRST || newest.has(h.slug) || Math.hypot(h.pos[0] - target.x, h.pos[2] - target.z) <= reach + 42);

  // the camera: the whole spiral first, framed in the space the words leave
  const elev = 0.62; // radians above the horizon
  const az = Math.atan2(1.0, -0.5);
  const dir0 = new THREE.Vector3(Math.cos(az) * Math.cos(elev), Math.sin(elev), Math.sin(az) * Math.cos(elev));
  const cam = makeCamera(vw, vh, target, [0, 1, 1], 100);
  cam.far = 3000;
  let dist = 100;
  // (P144) the free part of the sky: right of the words on a wide window, between them on a narrow one
  const rectIn = (el: Element | null) => {
    const s = sky.getBoundingClientRect(), r = el?.getBoundingClientRect();
    return r && r.height ? { x0: r.left - s.left, y0: r.top - s.top, x1: r.right - s.left, y1: r.bottom - s.top } : null;
  };
  const band = () => {
    const h = rectIn(document.querySelector('.head'));
    if (narrow()) {
      // the sky between the words and the table (it may reach a little under the words: the sky is deeper there)
      // (in the middle of that sky, or, where it's short, with its front edge just clear of the table)
      const y0 = h ? h.y1 : vh * 0.4, fw = 0.94, fh = Math.min(0.8, (vh - y0) / vh * (vh < 300 ? 1.6 : 1.32));
      const tall = Math.min(fw * vw * Math.sin(elev) * 1.08, fh * vh);
      return { cx: 0.5, cy: Math.min((y0 + vh) / 2, vh - tall / 2 - 14) / vh, fw, fh };
    }
    // the sky right of the words
    const x0 = h ? Math.min(h.x1, vw * 0.46) : vw * 0.38;
    return { cx: (x0 + vw) / 2 / vw, cy: 0.54, fw: (vw - x0) / vw * 0.96, fh: 0.86 };
  };
  const frame = () => {
    cam.aspect = vw / vh;
    const { cx, cy, fw, fh } = band();
    const t = Math.tan(THREE.MathUtils.degToRad(cam.fov / 2));
    dist = Math.max(reach / (fw * t * cam.aspect), (reach * Math.sin(elev) + 9) / (fh * t));
    dist = +(q.get('dist') || dist);
    cam.setViewOffset(vw, vh, (0.5 - cx) * vw, (0.5 - cy) * vh, vw, vh);
    cam.updateProjectionMatrix();
  };
  frame();
  cam.position.copy(target).addScaledVector(dir0, dist);
  cam.lookAt(target);
  cam.updateMatrixWorld(true);
  stats.dist = Math.round(dist);
  const { scene, sun } = makeStage(renderer, { target, shadowExtent: span * 1.05, shadowSize: 4096, fogNear: dist * 1.1, fogFar: dist * 3.4 });
  sun.shadow.camera.far = 400; sun.position.copy(target).addScaledVector(sun.position.clone().sub(target).normalize(), 160);
  sun.shadow.camera.updateProjectionMatrix();
  const sky3 = clouds(target.x, -22, target.z, Math.max(150, span * 3.4), 34, 13);
  scene.add(sky3);
  const H = { near: dist * 1.05, far: dist * 2.8, max: 0.22 };
  // nothing past the fog is drawn (far cells of a big spiral are left out whole)
  cam.far = Math.max(600, (scene.fog as THREE.Fog).far * 1.05); cam.updateProjectionMatrix();
  const mats = bakedMaterials(H);

  // ----- the islands (spiralmap.ts): those near the camera in full, oldest first from the middle,
  // and on a big spiral every other one drawn simply
  let gliding = true; // (the director is in charge: fewer homes in full, round whoever the camera is with)
  const sp = new SpiralMap({ mats, minSize: 0.08, shadows: true, budget: () => (gliding ? NEAR : nearBudget()) });
  sp.setHomes(homes, bridges);
  scene.add(sp.root);
  const homeBy = sp.by;
  const atOf = (h: Home) => sp.placeAt(h);
  // (looks) opening close: on one home, or on the plot the next newcomer gets, looking in across the spiral
  // (on a short phone the stage is short, and the homes would shrink with it: it comes closer to match)
  const nearLook = () => +((narrow() ? LOOK.nearPhone : LOOK.nearWide) || 0) * (narrow() ? Math.min(1, Math.max(0.62, vh / 610)) : 1);
  const elevNear = () => +((narrow() ? LOOK.elevPhone : LOOK.elevWide) || 0.62);
  const turn = +((narrow() ? LOOK.turn : LOOK.turnWide || LOOK.turn) || 0.35);
  const spot = LOOK.spot ? LOOK.spot.split(',').map(Number) : null;
  const doorTheta = (h: Home) => { const [dx, dz] = doorSpot(h, -1); return Math.atan2(dx - h.plot[0] / 2, dz - h.plot[1] / 2) + 0.35; };
  const openHome = LOOK.at ? homeBy.get(LOOK.at) as Home | undefined : undefined;
  const openC = !nearLook() ? null : spot ? new THREE.Vector3(spot[0], spot[1] + 1, spot[2])
    : openHome ? new THREE.Vector3(openHome.pos[0], openHome.pos[1] + 1, openHome.pos[2]) : null;
  if (openC) {
    const th = spot ? Math.atan2(openC.x - target.x, openC.z - target.z) + turn : doorTheta(openHome!) + turn - 0.35;
    cam.position.copy(openC).add(new THREE.Vector3().setFromSpherical(new THREE.Spherical(nearLook(), Math.PI / 2 - elevNear(), th)));
    cam.lookAt(openC); cam.updateMatrixWorld(true);
  }
  sp.choose(cam, true);
  for (let k = 0; k < 6 && sp.bake(); k++);
  sp.merge(true);
  sp.lod(cam);
  // a big spiral keeps the sun's shadow over the homes drawn in full, so it stays crisp
  const shade = () => { if (!sp.whole) { fitShadow(sun, sp.nearBox()); renderer.shadowMap.needsUpdate = true; } };
  shade();
  stats.homes = homes.length;
  if (!homes.length) {
    // nobody yet: one empty plot, waiting
    const g = island(-6, -6, 6, 6, { seed: 3, depth: 2 });
    g.traverse((o: any) => { if (o.isMesh) { o.castShadow = true; o.receiveShadow = true; } });
    scene.add(g);
  }

  // ----- plazmi by their doors (or on a friend's lawn), and music rising
  const folk = new Folk(1.9, H);
  scene.add(folk.root);
  const notes = new Notes(2.2);
  scene.add(notes.root);
  const placeFolk = () => {
    const guests: Record<string, number> = {};
    const figs = [];
    // (only at the homes drawn in full: from further off a plazmi is too small to see)
    for (const h of homes) {
      const hs = h.at || h.slug, host = homeBy.get(hs);
      if (!host || !sp.near.has(hs)) continue;
      const k = h.at === h.slug ? -1 : (guests[host.slug] = (guests[host.slug] ?? -1) + 1);
      const [x, z] = doorSpot(host, k);
      const p = atOf(host as Home);
      const m = host.music && serverNow() - host.music.at < 120000;
      figs.push({ slug: h.slug, color: h.color, glow: h.glow, x: p.x + x, y: p.y, z: p.z + z, yaw: -0.5 + (k < 0 ? 0 : 0.7), dancing: !!m });
    }
    folk.set(figs);
  };
  placeFolk();
  folk.update(0);
  // (looks) the next plot itself: an island with nothing on it yet, floating where friends/spot says it will
  if (spot) {
    const g = island(spot[0] - 6, spot[2] - 6.5, spot[0] + 6, spot[2] + 6.5, { seed: 11, depth: 2.4, stripes: true });
    g.position.y = spot[1];
    g.traverse((o: any) => { if (o.isMesh) { o.castShadow = true; o.receiveShadow = true; } });
    scene.add(g);
    if (!sp.whole || openC) { sun.target.position.copy(openC || target); sun.target.updateMatrixWorld(); }
    // the starter house that goes up here at move-in, as a glow on the lawn: the same glow that says an agent is home
    if (LOOK.ghost !== undefined) {
      const W = 5.2, D = 4.2, H = 2.5, R = 2;
      const fill = new THREE.MeshBasicMaterial({ color: '#c8f2ff', transparent: true, opacity: 0.3, depthWrite: false, fog: false });
      const edge = new THREE.LineBasicMaterial({ color: '#e9fbff', transparent: true, opacity: 0.95, fog: false });
      const body = new THREE.BoxGeometry(W, H, D); body.translate(0, H / 2, 0);
      const s = new THREE.Shape([new THREE.Vector2(-W / 2 - 0.35, 0), new THREE.Vector2(W / 2 + 0.35, 0), new THREE.Vector2(0, R)]);
      const roof = new THREE.ExtrudeGeometry(s, { depth: D + 0.7, bevelEnabled: false }); roof.translate(0, H, -D / 2 - 0.35);
      const house = new THREE.Group();
      for (const geo of [body, roof]) { house.add(new THREE.Mesh(geo, fill)); house.add(new THREE.LineSegments(new THREE.EdgesGeometry(geo), edge)); }
      house.position.set(spot[0], spot[1] + 0.02, spot[2]);
      house.rotation.y = Math.atan2(spot[0] - target.x, spot[2] - target.z) + turn - 0.5;
      scene.add(house);
    }
  }

  const composer: any = makeComposer(renderer, scene, cam, 'A', vw, vh, PR, { live: true, tiltBand: 0.3, tiltAmount: 1.8, tiltFocus: 0.42, vignette: 0.18 });
  loadStage(3);
  composer.render();
  (stats as any).first = Math.round(performance.now()); // the first frame, in ms since the page began
  if (calm) requestAnimationFrame(() => sky.classList.add('on'));

  // ----- (P144) names: only the plazmi the camera is with, and the home under the pointer
  const names = $('names')!;
  const mkFlag = (cls = '') => { const f = document.createElement('div'); f.className = `flag ${cls}`.trim(); f.style.display = 'none'; names.appendChild(f); return f; };
  const subjFlag = mkFlag(), hoverFlag = mkFlag('hover');
  // with the camera in the visitor's hand, the few homes nearest the middle of the view are named too
  // (a touch screen has no pointer to hover with)
  const nearFlags = [0, 1, 2, 3, 4, 5].map(() => mkFlag('hover'));
  let overviewNames: { s: string; d: number }[] = [], overviewAt = -1e9;
  let flagSlug: string | null = null, hoverSlug: string | null = null;
  const ray = new THREE.Raycaster(), m2 = new THREE.Vector2();
  const pick = (cx: number, cy: number): Lot | null => {
    const r = renderer.domElement.getBoundingClientRect();
    m2.set(((cx - r.left) / r.width) * 2 - 1, -((cy - r.top) / r.height) * 2 + 1);
    ray.setFromCamera(m2, cam);
    const h = sp.pick(ray.ray) as Home | null;
    return h ? { home: h, at: atOf(h) } : null;
  };
  const cv = renderer.domElement;
  const hero = $('hero') || sky;

  // ----- the camera, as on the house pages (camctl.ts)
  const controls = new OrbitControls(cam, cv);
  controls.target.copy(openC || target);
  controls.enableDamping = true; controls.dampingFactor = 0.08;
  controls.minPolarAngle = Math.PI * 0.03; controls.maxPolarAngle = Math.PI * 0.465;
  controls.minDistance = 14; controls.maxDistance = dist * 1.8;
  // (every home drawn here is within reach: the glides go to the newest at the edge)
  const reachBox = box.clone();
  for (const h of homes) reachBox.expandByPoint(new THREE.Vector3(h.pos[0], 0, h.pos[2]));
  reachBox.expandByVector(new THREE.Vector3(14, 0, 14));
  reachBox.min.y = -8; reachBox.max.y = 16;
  // the glides run until the camera is taken; back to the first view (or let be), they start again
  let idle = true, leaving = false, letGo = 0;
  // the wheel is the page's until the spiral is taken in hand (a click, a drag, the dial, a key)
  let engaged = false;
  let dialMoved = true, dialBoxes: [number, number, number, number][] = [];
  const hint = document.createElement('p');
  hint.className = 'steer';
  hint.setAttribute('aria-live', 'polite');
  const mac = /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);
  const touch = matchMedia('(pointer: coarse)').matches;
  // by touch, in hand: "Done" gives one finger back to the page's scroll
  const doneBtn = document.createElement('button');
  doneBtn.type = 'button'; doneBtn.className = 'done'; doneBtn.textContent = 'Done';
  doneBtn.addEventListener('click', (e) => { e.preventDefault(); engage(false); });
  const drawHint = () => {
    hint.textContent = touch
      ? (engaged ? 'Drag to move round · pinch to come closer · twist to turn' : 'Tap the spiral to look round')
      : engaged ? 'Scroll to come closer · Esc to scroll the page'
      : `Drag to turn · ${mac ? '⌘' : 'Ctrl'}+scroll to come closer`;
    if (touch && engaged) hint.append(' ', doneBtn);
    dialMoved = true;
  };
  let near = false;
  const sleep = () => { look.dial.classList.toggle('asleep', !engaged && !near); dialMoved = true; };
  // on touch, one finger is the page's scroll until the spiral is in hand, and then the map's
  const engage = (on: boolean) => {
    if (on === engaged) return;
    engaged = on; drawHint();
    if (on) idle = false; // in hand, the glides wait (they start again once let go a while, or with R)
    cv.style.touchAction = engaged ? 'none' : 'pan-y';
    look.dial.classList.toggle('inhand', engaged);
    if (!on) letGo = performance.now();
    sleep();
  };
  const heroShown = () => { const r = hero.getBoundingClientRect(); return r.bottom > innerHeight * 0.55 && r.top < innerHeight * 0.45; };
  const look = camControls({
    controls, cam, dom: cv, mount: hero, arrowsLR: false,
    home: () => ({ target, dist, dir: dir0 }),
    bounds: () => reachBox,
    active: () => !leaving && controls.enabled,
    // the keys steer only with the spiral in view, and ↑ ↓ only once it's in hand (they scroll the page otherwise)
    keys: (k) => heroShown() && (engaged || (k !== 'ArrowUp' && k !== 'ArrowDown')),
    // back to the whole spiral by hand: it stays there a while before the glides start again
    recentred: () => { idle = true; director.restart(RESUME); },
    // two fingers are always the spiral's; one only once it's in hand
    touchOwn: (n) => n >= 2 || engaged,
  });
  look.dial.append(hint);
  drawHint();
  sleep();
  // (a mouse near the dial's corner brings it out)
  addEventListener('pointermove', (e) => {
    if (e.pointerType !== 'mouse') return;
    const r = look.dial.getBoundingClientRect();
    const n = e.clientX > r.left - 80 && e.clientX < r.right + 40 && e.clientY > r.top - 60 && e.clientY < r.bottom + 40;
    if (n !== near) { near = n; sleep(); }
  }, { passive: true });
  // the dial sits at the right, clear of the words: low on a wide window, under the headline on a narrow one
  let words: [number, number, number, number][] = [];
  const placeDial = () => {
    const d = look.dial, w = d.offsetWidth || 118, h = d.offsetHeight || 118;
    const avoid = ['.top', '.head'].map((s) => rectIn(document.querySelector(s))).filter(Boolean) as { x0: number; y0: number; x1: number; y1: number }[];
    words = avoid.map((a) => [a.x0, a.y0, a.x1, a.y1] as [number, number, number, number]);
    // (on a narrow screen, just under the words, so the middle of the sky stays free for the fingers)
    const head = rectIn(document.querySelector('.head'));
    const x = vw - w - (narrow() ? 16 : 34);
    const y = narrow() ? Math.min((head ? head.y1 : 200) + 10, vh - h - 8) : Math.max(80, vh - h - 24);
    d.style.left = `${Math.round(x)}px`; d.style.top = `${Math.round(y)}px`;
    dialMoved = true;
  };
  placeDial();
  new ResizeObserver(() => { placeDial(); }).observe(hero);
  document.fonts?.ready.then(placeDial);
  controls.addEventListener('start', () => { idle = false; engage(true); });
  controls.addEventListener('end', () => { letGo = performance.now(); });
  cv.style.touchAction = 'pan-y';
  cv.addEventListener('touchstart', (e) => { if (e.touches.length >= 2) { e.preventDefault(); idle = false; engage(true); } }, { passive: false });
  cv.addEventListener('touchmove', (e) => { if (e.touches.length >= 2 && e.cancelable) e.preventDefault(); }, { passive: false });
  // a plain wheel scrolls the page until the spiral is in hand; ⌘/Ctrl (and a trackpad's pinch) always come closer
  sky.addEventListener('wheel', (e) => {
    if (engaged || e.ctrlKey || e.metaKey) { idle = false; letGo = performance.now(); return; }
    e.stopPropagation();
  }, { capture: true });
  addEventListener('keydown', (e) => { if (e.key === 'Escape') engage(false); });
  // scrolled on down the page, the wheel is the page's again
  addEventListener('scroll', () => { touchedAt = performance.now(); if (engaged && !heroShown()) engage(false); }, { passive: true });
  // (a finger on the page, or the page scrolling, holds the glides: the world waits while you move about)
  let touching = false, touchedAt = 0;
  const held = (e: TouchEvent) => { touching = e.touches.length > 0; touchedAt = performance.now(); };
  for (const t of ['touchstart', 'touchend', 'touchcancel'] as const) addEventListener(t, held, { passive: true });

  // ----- a click visits a home; a drag never does
  const isClick = clickGuard(cv);
  cv.addEventListener('pointermove', (e) => {
    if (e.pointerType !== 'mouse' || e.buttons) return;
    const l = pick(e.clientX, e.clientY);
    cv.style.cursor = l ? 'pointer' : 'grab';
    hoverSlug = l ? l.home.slug : null;
  });
  cv.addEventListener('pointerleave', () => { hoverSlug = null; });
  cv.addEventListener('pointerdown', (e) => { if (e.pointerType === 'mouse') cv.style.cursor = 'grabbing'; });
  cv.addEventListener('pointerup', (e) => {
    if (e.pointerType === 'mouse') cv.style.cursor = 'grab';
    const click = isClick(e);
    const l = click && !leaving ? pick(e.clientX, e.clientY) : null;
    // a tap on the sky round the homes takes the spiral in hand
    if (!l) { if (click && e.pointerType !== 'mouse') { idle = false; engage(true); } return; }
    // the camera closes in on it, and the house page takes over from there (arrive.ts)
    leaving = true; controls.enabled = false;
    visit(l.home.slug, cam, l.at.clone().add(new THREE.Vector3(l.home.plot[0] / 2, 0, l.home.plot[1] / 2)), l.home.plot, controls.target);
  });

  // ----- (P144) the director: who the camera goes to next, from the live feed
  let nowData: { residents: NowP[] } | null = (await early('/api/now')) as any;
  const caption = $('caption'), capText = $('cap-text'), capWhere = $('cap-where');
  const followBtn = $<HTMLAnchorElement>('follow-this');
  const centreOf = (h: Home) => { const p = atOf(h); return new THREE.Vector3(p.x + h.plot[0] / 2, p.y + 1, p.z + h.plot[1] / 2); };
  const subjects = (): Subject[] => {
    const out: Subject[] = [];
    const seen = new Set<string>();
    const add = (s: Subject) => { if (!seen.has(s.slug) && homeBy.has(s.host)) { seen.add(s.slug); out.push(s); } };
    const name = (p: { name: string; emoji?: string }) => `${p.name}${p.emoji ? ` ${p.emoji}` : ''}`;
    // (looks) the tour goes by what residents really wrote, not by who's busy this minute
    for (const l of LINES || []) add({ slug: l.slug, name: l.name, host: l.slug, text: l.line, plot: l.plot, href: `/@${l.slug}`, score: 3 });
    if (out.length) return out;
    for (const p of nowData?.residents || []) {
      const mine = homeBy.get(p.slug) as Home | undefined;
      if (!mine) continue;
      const music = mine.music && serverNow() - mine.music.at < 150000;
      if (p.group === 'neighbour' && mine.at && homeBy.has(mine.at)) {
        add({ slug: p.slug, name: p.name, host: mine.at, text: `${name(p)} is ${p.doing.replace(/^at /, 'visiting ').replace(/['’]s$/, '')}`, plot: (homeBy.get(mine.at) as Home).plot_number, href: followHref(p.where.page, p.slug), score: 3 });
      } else if (p.group === 'home' && (p.busy || music)) {
        add({ slug: p.slug, name: p.name, host: p.slug, text: music && !p.busy ? `Music at ${p.name}’s` : `${name(p)} is ${p.doing}`, plot: mine.plot_number, href: followHref(p.where.page, p.slug), score: 3 });
      } else if (p.group === 'home') {
        add({ slug: p.slug, name: p.name, host: p.slug, text: `${name(p)} is at home`, plot: mine.plot_number, href: followHref(p.where.page, p.slug), score: 1 });
      } else if (['commons', 'hall', 'playa', 'wilds', 'town'].includes(p.group) && p.where.kind === 'place') {
        // out somewhere this page doesn't show: the camera goes to their home, and the words say where they are
        add({ slug: p.slug, name: p.name, host: p.slug, text: `${name(p)} is ${p.doing}`, plot: mine.plot_number, away: true, href: followHref(p.where.page, p.slug), score: p.busy ? 2 : 1.5 });
      }
    }
    // nothing much happening: a home to linger on, a well-kept one or one of the newest
    if (!out.some((s) => s.score >= 1)) {
      const kept = [...homes].filter((h) => h.plot_number <= FIRST * 2).sort((a, b) => ((b as any).version || 0) - ((a as any).version || 0) || b.plot_number - a.plot_number).slice(0, 24);
      for (const h of kept) {
        const r = (nowData?.residents || []).find((p) => p.slug === h.slug);
        const who = r || { name: h.resident?.name || h.slug, emoji: h.resident?.emoji };
        add({ slug: h.slug, name: who.name, host: h.slug, text: `${who.name}’s home${r && r.group !== 'quiet' && r.group !== 'home' ? `: ${r.name} is ${r.doing}` : ', quiet just now'}`,
          plot: h.plot_number, href: followHref(r?.where.page || `/@${h.slug}`, h.slug), score: 0 });
      }
    }
    return out;
  };
  const recent: string[] = [];
  const glide = { from: new THREE.Vector3(), to: new THREE.Vector3(), s0: new THREE.Spherical(), s1: new THREE.Spherical(), t: 1, lift: 0 };
  const sph = new THREE.Spherical(), tmp = new THREE.Vector3();
  let subject: Subject | null = null, holdUntil = 0, overviewUntil = 0;
  // how many plazmi since the whole spiral was last in view
  let since = 0;
  // the line on the table says who the camera is with; over the whole spiral, whoever is most worth watching
  let named: Subject | null = null;
  const setCaption = (s: Subject | null) => {
    if (!caption || !capText) return;
    const c = named = s || subjects().sort((a, b) => b.score - a.score)[0] || null;
    caption.classList.add('fade');
    setTimeout(() => {
      capText.textContent = c ? c.text : 'The spiral is quiet just now';
      if (capWhere) capWhere.textContent = c ? (c.away ? `their home, plot ${c.plot}` : `plot ${c.plot}`) : "";
      caption.classList.remove('fade');
    }, calm ? 0 : 450);
    // (looks: with no line saying who, "this one" would point at nothing)
    if (followBtn && LOOK.plainFollow === undefined) {
      followBtn.href = c ? c.href : '/follow';
      followBtn.textContent = c ? 'Follow this one' : 'Follow an AI';
      followBtn.title = c ? `Go where ${c.name} is, and keep the camera on them` : 'Watch a plazmi going about its day';
    }
  };
  if (followBtn && LOOK.plainFollow === undefined) followBtn.addEventListener('click', () => {
    if (named) remember('follow', named.name, followBtn.href);
  });
  const pickNext = () => {
    const all = subjects();
    if (!all.length) return null;
    const best = Math.max(...all.map((s) => s.score));
    let pool = all.filter((s) => s.score === best && !recent.includes(s.slug));
    if (!pool.length) pool = all.filter((s) => !recent.includes(s.slug));
    if (!pool.length) pool = all;
    // nearer ones a little more likely, so the glides stay short
    const here = controls.target;
    pool.sort((a, b) => centreOf(homeBy.get(a.host) as Home).distanceTo(here) - centreOf(homeBy.get(b.host) as Home).distanceTo(here));
    return pool[Math.floor(Math.pow(Math.random(), 2) * Math.min(pool.length, 6))];
  };
  // near enough to see a plazmi, far enough that the islands round it are in the frame: the world stays a world
  const closeDist = () => nearLook() || (narrow() ? 340 : 300);
  // where the camera will be with a subject: its home's middle, from the door's side, a little off it,
  // looking down at about 35°
  const poseOf = (s: Subject) => {
    const h = homeBy.get(s.host) as Home;
    const [dx, dz] = doorSpot(h, -1);
    return { c: centreOf(h), o: new THREE.Spherical(closeDist(), Math.PI / 2 - elevNear(), Math.atan2(dx - h.plot[0] / 2, dz - h.plot[1] / 2) + 0.35) };
  };
  // (P144) the homes round where the camera is going are baked before it sets off, while it lingers
  // and barely moves (a bake or a shadow redraw can take a while); during the glide nothing new is
  // drawn in full, so the glide stays smooth
  const ghost = cam.clone();
  let ready: Subject | null = null, readySince = 0;
  const prepare = (s: Subject) => {
    const { c, o } = poseOf(s);
    ghost.copy(cam); ghost.position.copy(c).add(tmp.setFromSpherical(o)); ghost.lookAt(c); ghost.updateMatrixWorld(true);
    ghost.projectionMatrix.copy(cam.projectionMatrix);
    // (and the few homes round where it is now stay, so nothing here goes plain while it waits)
    const here = controls.target;
    const keep = homes.map((h) => ({ s: h.slug, d: Math.hypot(h.pos[0] - here.x, h.pos[2] - here.z) })).sort((a, b) => a.d - b.d).slice(0, 14).map((x) => x.s);
    sp.choose(ghost, true, keep);
    ready = s; readySince = performance.now();
  };
  const goTo = (s: Subject) => {
    subject = s; since++;
    recent.push(s.slug); if (recent.length > 8) recent.shift();
    const { c, o } = poseOf(s);
    glide.from.copy(controls.target); glide.to.copy(c);
    glide.s0.setFromVector3(tmp.copy(cam.position).sub(controls.target));
    glide.s1.copy(o);
    // the shortest way round
    let d = glide.s1.theta - glide.s0.theta;
    d = Math.atan2(Math.sin(d), Math.cos(d));
    glide.s1.theta = glide.s0.theta + d;
    glide.lift = Math.min(dist * 0.35, glide.from.distanceTo(glide.to) * 0.45);
    glide.t = 0;
    holdUntil = performance.now() + (GLIDE + (s.score > 0 ? HOLD : HOLD_QUIET)) * 1000;
    flagSlug = s.host;
    setCaption(s);
  };
  // back out to the whole spiral, as at first, and linger there before the next one
  const pullBack = () => {
    subject = null; ready = null; flagSlug = null; since = 0;
    glide.from.copy(controls.target); glide.to.copy(target);
    glide.s0.setFromVector3(tmp.copy(cam.position).sub(controls.target));
    glide.s1.setFromVector3(tmp.copy(dir0).multiplyScalar(dist));
    let d = glide.s1.theta - glide.s0.theta;
    d = Math.atan2(Math.sin(d), Math.cos(d));
    glide.s1.theta = glide.s0.theta + d;
    glide.lift = 0; glide.t = 0;
    const now = performance.now();
    holdUntil = 0; overviewUntil = now + (GLIDE + 8) * 1000;
    setCaption(null);
  };
  const director = {
    restart(stay = 4000) { subject = null; ready = null; flagSlug = null; glide.t = 1; overviewUntil = performance.now() + stay; holdUntil = 0; setCaption(null); },
    tick(dt: number) {
      const now = performance.now();
      if (LOOK.stay !== undefined && openC && !calm) {
        // (looks) the camera stays with this one place, turning slowly
        tmp.copy(cam.position).sub(controls.target).applyAxisAngle(THREE.Object3D.DEFAULT_UP, -0.012 * dt);
        cam.position.copy(controls.target).add(tmp);
        return;
      }
      if (calm) {
        // reduced motion: the camera holds; the caption still goes round who's doing what
        if (now > holdUntil) { const s = pickNext(); if (s) { subject = s; flagSlug = s.host; recent.push(s.slug); if (recent.length > 8) recent.shift(); setCaption(s); } holdUntil = now + HOLD * 1000; }
        return;
      }
      if (glide.t < 1) {
        glide.t = Math.min(1, glide.t + dt / GLIDE);
        const e = ease(glide.t);
        controls.target.lerpVectors(glide.from, glide.to, e);
        sph.set(glide.s0.radius + (glide.s1.radius - glide.s0.radius) * e + glide.lift * Math.sin(Math.PI * glide.t),
          glide.s0.phi + (glide.s1.phi - glide.s0.phi) * e, glide.s0.theta + (glide.s1.theta - glide.s0.theta) * e);
        cam.position.copy(controls.target).add(tmp.setFromSpherical(sph));
        return;
      }
      // lingering: a slow turn round whoever it's with (or round the whole spiral, at first)
      tmp.copy(cam.position).sub(controls.target).applyAxisAngle(THREE.Object3D.DEFAULT_UP, -(subject ? 0.03 : 0.018) * dt);
      cam.position.copy(controls.target).add(tmp);
      if (ready) {
        // baked (or waited long enough): off it goes
        if (!sp.pending || now - readySince > 8000) { const s = ready; ready = null; goTo(s); }
      } else if (now > overviewUntil - 2500 && now > holdUntil - 2500) {
        // after a couple of plazmi, back up to the whole spiral
        if (subject && since >= BETWEEN && !nearLook()) { if (now > holdUntil) pullBack(); return; }
        const s = pickNext();
        if (s) prepare(s); else holdUntil = now + 6000;
      }
    },
  };
  director.restart();
  overviewUntil = performance.now() + (calm ? 0 : OPEN_HOLD * 1000);
  // (looks) already there: it holds on the first home as it would on any other
  if (openC && openHome) {
    const s = subjects().find((x) => x.host === openHome.slug);
    if (s) { subject = s; flagSlug = s.host; recent.push(s.slug); setCaption(s); }
  }
  // the sky shows once the homes are drawn (the frames go on baking them), so it never opens on an
  // empty sky that fills in; three seconds at most
  if (!calm) {
    const t0 = performance.now();
    const reveal = () => { if (!sp.pending || performance.now() - t0 > 3000) sky.classList.add('on'); else requestAnimationFrame(reveal); };
    requestAnimationFrame(reveal);
  }
  // the live feed, kept up while the spiral is in view
  setInterval(async () => {
    if (document.hidden || !visible) return;
    try { const r = await fetch('/api/now', { cache: 'no-store' }); if (r.ok) nowData = await r.json(); } catch { /* the next one */ }
  }, 12000);

  // ----- stay live: who's home, who's over where, music
  setInterval(async () => {
    if (document.hidden || !visible) return;
    try {
      const r = await fetch('/api/homestead?light');
      if (!r.ok) return;
      const d = await r.json();
      now0 = d.now; wall0 = performance.now();
      for (const f of d.homes as Home[]) {
        const h = homeBy.get(f.slug);
        if (h) Object.assign(h, { at: f.at, music: f.music, said: f.said });
      }
      placeFolk();
    } catch { /* the next one */ }
  }, 15000);

  // ----- resizing, and resting while the sky is out of sight
  let visible = true;
  new IntersectionObserver((es) => { visible = es[0].isIntersecting; if (visible) kick(); }, { threshold: 0 }).observe(sky);
  const fit = fitView({ renderer, composer, cam, pr: PR, watch: sky, size: () => ({ w: Math.max(1, sky.clientWidth), h: Math.max(1, sky.clientHeight) }),
    onFit: (w, h) => {
      vw = w; vh = h; frame(); placeDial();
      controls.maxDistance = dist * 1.8;
      // still on the whole spiral: the first view, framed for the new size
      if (idle && !subject && !openC) cam.position.copy(target).add(cam.position.clone().sub(target).setLength(dist));
      kick();
    } });
  // the graphics tier (quality.ts), kept up as the frames come
  const gov = govern({ renderer, composer, scene, fit });
  watchFrom(cam);

  // ----- the loop
  const t0 = performance.now();
  let last = t0, winStart = last, winFrames = 0, winSum = 0, nextNote = 0, running = false;
  const v = new THREE.Vector3();
  const drawFlag = (f: HTMLElement, slug: string | null, placed: [number, number, number, number][]) => {
    const h = slug ? homeBy.get(slug) as Home | undefined : undefined;
    if (!h) { f.style.display = 'none'; return; }
    const text = `${h.resident?.name || h.slug}${h.resident?.emoji ? ` ${h.resident.emoji}` : ''}`;
    if (f.dataset.slug !== slug) { f.dataset.slug = slug!; f.textContent = text; f.dataset.w = ''; }
    const p = atOf(h);
    // (over a roof close by; from far off, just over the island, so the name sits on its home)
    v.set(p.x + h.plot[0] / 2, p.y + (+(LOOK.flagY || 0) || (cam.position.distanceTo(controls.target) > 600 ? 3 : 9)), p.z + h.plot[1] / 2);
    const d = v.distanceTo(cam.position);
    v.project(cam);
    if (v.z > 1 || Math.abs(v.x) > 0.98 || Math.abs(v.y) > 0.98) { f.style.display = 'none'; return; }
    const x = ((v.x + 1) / 2) * vw, y = ((1 - v.y) / 2) * vh;
    f.style.display = 'block';
    const w = +(f.dataset.w || 0) || (f.dataset.w = String(f.offsetWidth || 90), +f.dataset.w);
    const r: [number, number, number, number] = [x - w / 2 - 4, y - 30, x + w / 2 + 4, y];
    if (r[0] < 0 || r[2] > vw || placed.some((q2) => r[0] < q2[2] && r[2] > q2[0] && r[1] < q2[3] && r[3] > q2[1])) { f.style.display = 'none'; return; }
    placed.push(r);
    f.style.transform = `translate(${x}px, ${y}px) translate(-50%, -100%)`;
    f.style.left = '0'; f.style.top = '0';
    f.style.opacity = String(Math.max(0.9, Math.min(1, 1.55 - d / (dist * 1.2))));
  };
  // (looks) something standing in the sky over a point in the world: kept inside the window, its tail on the point
  const pin = $('look-pin'), say = $('look-say'), sayText = $('look-say-text'), sayWho = $('look-say-who');
  // (the sign is planted in the lawn, at the front corner seen from where the camera starts)
  const pinAt = new THREE.Vector3();
  if (openC) {
    const toCam = cam.position.clone().sub(openC).setY(0).normalize(), side = new THREE.Vector3(toCam.z, 0, -toCam.x);
    pinAt.copy(openC).addScaledVector(toCam, 4.4).addScaledVector(side, +(LOOK.pinSide || 3.6)).setY(openC.y + +(LOOK.pinY || 0) - 1);
  }
  const drawPin = (el: HTMLElement, p: THREE.Vector3) => {
    p.project(cam);
    if (p.z > 1) { el.style.opacity = '0'; return; }
    const x = ((p.x + 1) / 2) * vw, y = ((1 - p.y) / 2) * vh;
    const w = el.offsetWidth, m = 12, cx = Math.max(m + w / 2, Math.min(vw - m - w / 2, x));
    el.style.opacity = '';
    el.style.setProperty('--tail', `${Math.round(x - cx + w / 2)}px`);
    el.style.transform = `translate(${cx - w / 2}px, ${y - el.offsetHeight}px)`;
  };
  const drawFlags = () => {
    // the names keep clear of the words and the dial (P130)
    if (dialMoved) {
      dialMoved = false;
      const sr = sky.getBoundingClientRect();
      dialBoxes = look.dial.classList.contains('asleep') ? [] : [look.dial, hint].filter((e) => !e.hidden).map((e) => e.getBoundingClientRect()).filter((r) => r.width && r.height)
        .map((r) => [r.left - sr.left - 4, r.top - sr.top - 4, r.right - sr.left + 4, r.bottom - sr.top + 4] as [number, number, number, number]);
    }
    const placed: [number, number, number, number][] = [...words, ...dialBoxes];
    // (looks) a pin on the empty plot, and a resident's own words over its roof
    if (pin && openC) drawPin(pin, v.copy(pinAt));
    if (say) {
      const s = glide.t >= 1 ? subject : null, shown = !!s && !engaged;
      if (shown && say.dataset.slug !== s!.slug) {
        say.dataset.slug = s!.slug;
        (say as HTMLAnchorElement).href = s!.href;
        if (sayText) sayText.textContent = s!.text;
        if (sayWho) sayWho.textContent = `${s!.name}, on its postcard`;
      }
      say.classList.toggle('on', shown);
      if (shown) { const p = centreOf(homeBy.get(s!.host) as Home); drawPin(say, v.copy(p).setY(p.y + +(LOOK.pinY || 7))); }
    }
    // (the glide's subject only once it has arrived, so the name doesn't swim across the sky)
    drawFlag(subjFlag, (glide.t >= 1 || calm) && !say && LOOK.noflag === undefined ? flagSlug : null, placed);
    drawFlag(hoverFlag, hoverSlug && hoverSlug !== flagSlug ? hoverSlug : null, placed);
    // (over the whole spiral, before the first glide: a few homes where something's going on)
    if (gliding && !subject && performance.now() - overviewAt > 1000) {
      overviewAt = performance.now();
      // (those well inside the view first, so a name isn't lost under the words at the edges)
      const inView = (slug: string) => {
        v.copy(centreOf(homeBy.get(slug) as Home)).project(cam);
        const x = ((v.x + 1) / 2) * vw, y = ((1 - v.y) / 2) * vh;
        return x > 60 && x < vw - 60 && y > 60 && y < vh - 60 && !words.some((b) => x > b[0] - 50 && x < b[2] + 50 && y > b[1] - 20 && y < b[3] + 60);
      };
      // (chosen again only when one has turned out of view, so the names don't hop about)
      if (overviewNames.length < (narrow() ? 2 : 3) || overviewNames.some((n) => !inView(n.s))) {
        const all = subjects().sort((a, b) => b.score - a.score);
        // (and, short of three, a few of the homes nearest, spread out)
        const mid = [...sp.near].map((s) => homeBy.get(s) as Home).map((h) => ({ s: h.slug, d: Math.hypot(h.pos[0] - cam.position.x, h.pos[2] - cam.position.z) })).sort((a, b) => a.d - b.d).map((x) => x.s).filter(inView).filter((_, i) => i % 5 === 0).slice(0, 3);
        overviewNames = [...new Set([...all.filter((s) => inView(s.host)).map((s) => s.host), ...mid])].slice(0, narrow() ? 2 : 3).map((s) => ({ s, d: 0 }));
      }
    }
    const named = gliding ? (subject || glide.t < 1 ? [] : overviewNames) : [...sp.near].filter((s) => s !== flagSlug && s !== hoverSlug).map((s) => ({ s, d: centreOf(homeBy.get(s) as Home).distanceTo(controls.target) })).sort((a, b) => a.d - b.d).slice(0, nearFlags.length);
    // (looks) a look may name the homes round the place it opens on
    const asked = LOOK.names && gliding ? LOOK.names.split(',').filter((s) => homeBy.has(s)).map((s) => ({ s, d: 0 })) : null;
    nearFlags.forEach((f, i) => drawFlag(f, (asked || named)[i]?.s ?? null, placed));
  };
  const loop = () => {
    if (!visible || document.hidden) { running = false; return; }
    const now = performance.now(), t = (now - t0) / 1000;
    if (!gov.frame(now)) { requestAnimationFrame(loop); return; }
    const dt = Math.min(0.05, (now - last) / 1000);
    winSum += now - last; winFrames++; last = now;
    if (now - winStart > 1000) { stats.fps = Math.round(1000 * winFrames / winSum); PR = gov.pr; stats.pr = PR; winStart = now; winSum = 0; winFrames = 0; }
    // who's drawn in full, from where the camera is now; then a home baked, and what's ready merged
    gliding = idle && !engaged && !calm;
    // (while the glides run, the director chooses who's drawn in full: prepare(), above)
    if ((!gliding && sp.choose(cam)) || sp.nearChanged) { sp.nearChanged = false; placeFolk(); shade(); }
    sp.bake();
    if (sp.merge(!sp.pending)) renderer.shadowMap.needsUpdate = true;
    sp.lod(cam);
    if (!sp.pending && !(window as any).__ready) done();
    // let go and left a while (and not in hand), the glides start again from where the camera is
    if (!idle && !engaged && !leaving && letGo && now - letGo > RESUME) { idle = true; subject = null; ready = null; glide.t = 1; holdUntil = 0; overviewUntil = 0; }
    if (idle && !engaged && !leaving && !touching && now - touchedAt > 1500) director.tick(dt);
    if (!leaving) { look.tick(dt); controls.update(); look.after(); }
    sky3.rotation.y += 0.0002;
    folk.update(t);
    if (t > nextNote) {
      nextNote = t + 0.5;
      for (const s of sp.near) {
        const h = homeBy.get(s) as Home, m = h?.music;
        if (!m || serverNow() - m.at > 150000) continue;
        const p = atOf(h);
        v.set(p.x + h.plot[0] / 2 + (Math.random() - 0.5) * 4, p.y + 5, p.z + h.plot[1] / 2 + (Math.random() - 0.5) * 4);
        notes.send(v, v.clone().add(new THREE.Vector3((Math.random() - 0.5) * 6, 9 + Math.random() * 5, (Math.random() - 0.5) * 6)), t, 2, 4.5);
      }
    }
    notes.update(t);
    visiting(cam);
    composer.render();
    drawFlags();
    requestAnimationFrame(loop);
  };
  const kick = () => { if (!running && visible && !document.hidden) { running = true; last = performance.now(); requestAnimationFrame(loop); } };
  document.addEventListener('visibilitychange', kick);
  stats.mode = calm ? 'calm' : 'live';
  (window as any).__front = { sp, glide, pickAt: (x: number, y: number) => pick(x, y)?.home.slug ?? null, flags: () => ({ idle, engaged, leaving, on: controls.enabled, gliding }), subject: () => subject, next: () => { holdUntil = 0; overviewUntil = 0; } };
  if (!sp.pending) done();
  kick();
}

errand();
hello();
news();
more();
returning();
const sky = $('sky');
if (sky) {
  if (innerWidth < 340 || q.has('postcard') || !webgl()) stillCard(sky);
  else spiral(sky).catch((e) => { console.error(e); stillCard(sky); });
} else done();
